import { generatePath, useNavigate } from 'react-router';
import type { CardMenuItem } from '@tempmee/core';
import { AdditionalDetails, LargeShiftDetails } from '@tempmee/react-blocks';
import { OutlinePencilIcon, OutlineTrashIcon } from '@tempmee/react-icons';
import history from '~/browserHistory';
import AnimatedCloseCircle from '~/components/shared/AnimatedCloseCircle';
import { CancelShiftConfirmDialog } from '~/components/shared/CancelShiftConfirmDialog';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useStandardCancelFlow } from '~/hooks/useStandardCancelFlow';
import { routes } from '~/routes';
import { getStandardCancelFlow } from '~/services/posthog/FeatureFlagUtils';
import type { IShiftDetailsPageProps } from '../shiftDetailsPages';
import { useCancelShiftPopup } from '../shared/hooks/useCancelShiftPopup';
import { toAdditionalDetailsRows } from '../shared/utils/shiftPanelsProps';
import { DetailsPageShell } from '~/components/shared/DetailsPage';
import { InvitePanel } from './components/InvitePanel';
import {
  toShiftSummary,
  type TOpenShiftJob,
} from './utils/openShiftDetailsMappers';

const CANCEL_DESCRIPTION =
  'Cancelling an open shift means that it will no longer be available to professionals around ' +
  'your office to accept and potentially work.';

/**
 * OPEN Shift Details, as registered in `SHIFT_DETAILS_PAGES`.
 *
 * Composed entirely from design-system blocks — Invite Professionals on the
 * left, the shift summary and Additional Information on the right — with this
 * component owning only the data mapping, the header actions and the flows they
 * open. Reached through `ShiftDetailsRedesignGate`, which has already loaded the
 * job and matched its status, so there is no loading or not-found state here.
 *
 * Onsite interviews never reach it: the registry keeps those on the legacy
 * screen (`shiftDetailsPageForJob`).
 */
export function OpenShiftDetailsPage({ job }: IShiftDetailsPageProps) {
  const openShift = job as TOpenShiftJob;
  const navigate = useNavigate();
  const cancelPopup = useCancelShiftPopup(job.id);
  const isStandardCancelFlow = useStandardCancelFlow();

  const actions: CardMenuItem[] = [
    {
      label: 'Edit',
      icon: <OutlinePencilIcon />,
      testID: 'shift-details-edit-action',
      onSelect: () => history.push(generatePath(routes.editOpenJob, { jobId: job.id })),
    },
    {
      label: 'Cancel',
      icon: <OutlineTrashIcon />,
      testID: 'shift-details-cancel-action',
      onSelect: () => {
        // Records the A/B exposure; the popup itself is picked by the render-time peek.
        getStandardCancelFlow();
        cancelPopup.open();
      },
    },
  ];

  return (
    <>
      <DetailsPageShell
        testIDPrefix="shift-details"
        actionsLabel="Shift actions"
        title="Open Shift Details"
        actions={actions}
        onBack={() => navigate(-1)}
        summary={<LargeShiftDetails {...toShiftSummary(openShift)} />}
        primary={<InvitePanel job={job} />}
        sidebar={
          <AdditionalDetails
            variant="open-confirm"
            {...toAdditionalDetailsRows(openShift)}
            defaultOpen={false}
          />
        }
      />

      {cancelPopup.isOpen &&
        (isStandardCancelFlow ? (
          <CancelShiftConfirmDialog
            isInterview={false}
            isPending={cancelPopup.isCancelling}
            onClose={cancelPopup.close}
            onConfirm={cancelPopup.confirm}
          />
        ) : (
          <ConfirmPopup
            Icon={() => <AnimatedCloseCircle />}
            title="Are you sure you want to cancel?"
            description={CANCEL_DESCRIPTION}
            leftButtonText="Go Back"
            leftButtonAction={cancelPopup.close}
            // Go Back is the popup's only dismissal — it is given no `closePopup`.
            leftButtonDisabled={cancelPopup.isCancelling}
            leftButtonDataCy="cancel-popup-go-back"
            rightButtonText="Cancel Shift"
            rightButtonDataCy="confirm-cancel-shift"
            rightButtonLoading={cancelPopup.isCancelling}
            rightButtonAction={cancelPopup.confirm}
          />
        ))}
    </>
  );
}
